import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { stripeApi } from '@/lib/api';
import CardSetupForm from '@/components/CardSetupForm';
import { Truck, Users, ArrowLeft, Loader2, MapPin, CreditCard, Gift, CheckCircle } from 'lucide-react';

const RADIUS_OPTIONS = [
  { value: '5', label: '5 km' },
  { value: '10', label: '10 km' },
  { value: '25', label: '25 km' },
  { value: '50', label: '50 km' },
  { value: '100', label: '100 km' },
  { value: '250', label: '250 km' },
  { value: '500', label: '500 km (Regional)' },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('customer');
  const [registrationStep, setRegistrationStep] = useState(1); // 1 = details, 2 = card setup (providers only)
  const [registeredUser, setRegisteredUser] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    company_name: '',
    notification_postcode: '',
    notification_radius_km: '50',
    equipment_types: [],
    service_areas: []
  });

  useEffect(() => {
    const role = searchParams.get('role');
    if (role === 'provider') {
      setActiveTab('provider');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = {
        ...formData,
        role: activeTab,
        equipment_types: formData.equipment_types.length > 0 
          ? formData.equipment_types 
          : ['Flatbed', 'Gooseneck'],
        service_areas: formData.service_areas.length > 0 
          ? formData.service_areas 
          : ['Texas', 'Oklahoma']
      };
      
      const user = await register(userData);
      
      if (activeTab === 'provider') {
        // For providers, move to card setup step
        setRegisteredUser(user);
        setRegistrationStep(2);
        toast.success('Account created! Now set up your payment method.');
      } else {
        // For customers, go straight to dashboard
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCardSuccess = async (paymentMethodId, setupIntentId) => {
    try {
      // Save the payment method to the user's account
      await stripeApi.confirmPaymentMethod(paymentMethodId, setupIntentId);
      toast.success('Payment method saved! Your 14-day free trial has started.');
      navigate('/provider');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payment method');
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-secondary relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1722518015930-37a511eaa610?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxmcmVpZ2h0JTIwbWFwJTIwdGVjaG5vbG9neSUyMGFic3RyYWN0fGVufDB8fHx8MTc3MDQ1NjAwMHww&ixlib=rb-4.1.0&q=85)`
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center">
              <Truck className="w-7 h-7 text-white" />
            </div>
            <span className="font-bold text-2xl text-white" style={{ fontFamily: 'Barlow Condensed' }}>
              THE HOTSHOT<br />MARKETPLACE
            </span>
          </Link>
          
          <div>
            <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              JOIN THE<br />
              <span className="text-primary">MARKETPLACE</span>
            </h1>
            <p className="text-gray-400 max-w-md">
              Whether you need freight moved or you're ready to haul, 
              THM connects the right people at the right time.
            </p>
          </div>
          
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} The Hotshot Marketplace
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
              THE HOTSHOT MARKETPLACE
            </span>
          </div>

          <AnimatePresence mode="wait">
            {registrationStep === 1 ? (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  CREATE ACCOUNT
                </h2>
                <p className="text-muted-foreground mb-6">
                  Choose your account type to get started
                </p>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="customer" className="gap-2" data-testid="customer-tab">
                      <Users className="w-4 h-4" />
                      Customer
                    </TabsTrigger>
                    <TabsTrigger value="provider" className="gap-2" data-testid="provider-tab">
                      <Truck className="w-4 h-4" />
                      Provider
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Free Trial Banner for Providers */}
                {activeTab === 'provider' && (
                  <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                    <div className="flex items-center gap-3">
                      <Gift className="w-6 h-6 text-green-500 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-green-600">14-Day Free Trial</p>
                        <p className="text-sm text-muted-foreground">
                          Start quoting on jobs immediately. No charges during trial.
                          Only $9.95/week after trial ends.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      placeholder="John Smith"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                      data-testid="register-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+61 4XX XXX XXX"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                data-testid="register-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                data-testid="register-password-input"
              />
            </div>

            {activeTab === 'provider' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name (Optional)</Label>
                  <Input
                    id="company_name"
                    placeholder="Your Trucking Co."
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    data-testid="register-company-input"
                  />
                </div>
                
                <div className="p-4 bg-muted/50 rounded-sm space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <MapPin className="w-4 h-4 text-primary" />
                    Notification Area
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Get notified for jobs picking up or delivering near your location
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="notification_postcode">Your Postcode</Label>
                      <Input
                        id="notification_postcode"
                        placeholder="e.g., 4305"
                        value={formData.notification_postcode}
                        onChange={(e) => setFormData({ ...formData, notification_postcode: e.target.value })}
                        maxLength={4}
                        data-testid="register-postcode-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="notification_radius">Radius</Label>
                      <Select 
                        value={formData.notification_radius_km} 
                        onValueChange={(value) => setFormData({ ...formData, notification_radius_km: value })}
                      >
                        <SelectTrigger data-testid="register-radius-select">
                          <SelectValue placeholder="Select radius" />
                        </SelectTrigger>
                        <SelectContent>
                          {RADIUS_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </>
            )}

            <Button 
              type="submit" 
              className="w-full btn-active" 
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                `Create ${activeTab === 'customer' ? 'Customer' : 'Provider'} Account`
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Sign in
            </Link>
          </p>
              </motion.div>
            ) : (
              /* Step 2: Card Setup for Providers */
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm text-green-600 font-medium">Account Created!</span>
                </div>

                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  SET UP PAYMENT
                </h2>
                <p className="text-muted-foreground mb-6">
                  Add a card to start your 14-day free trial. You won't be charged until the trial ends.
                </p>

                {/* Trial Benefits */}
                <div className="mb-6 p-4 bg-muted/50 rounded-sm border border-border">
                  <div className="flex items-center gap-2 mb-3">
                    <Gift className="w-5 h-5 text-primary" />
                    <span className="font-medium">Your Free Trial Includes:</span>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Full access to all available jobs
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Submit unlimited quotes
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      No subscription fee for 14 days
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Cancel anytime before trial ends
                    </li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                    Note: 10% platform fee still applies when you complete jobs during trial.
                  </p>
                </div>

                <CardSetupForm 
                  onSuccess={handleCardSuccess}
                  onError={(error) => toast.error(error.message)}
                />

                {/* Skip option for testing */}
                <Button 
                  variant="ghost" 
                  className="w-full mt-4 text-muted-foreground"
                  onClick={() => {
                    toast.success('Skipped card setup. You can add a card later from your dashboard.');
                    navigate('/provider');
                  }}
                >
                  Skip for now (testing only)
                </Button>

                <p className="text-center text-xs text-muted-foreground mt-6">
                  By adding your card, you agree to be charged $9.95/week after your trial ends unless you cancel.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
